/**
 * `/ai/usage` — month-to-date AI cost dashboard (#19 AC #7).
 *
 * Renders:
 *   - Large numeric display: month-to-date spend (USD), generation count,
 *     and configured budget.
 *   - Daily-spend sparkline (small inline SVG — no external charting
 *     library per the issue brief).
 *   - Top-10 most expensive generations table (MUI `<Table>` to match
 *     the existing Pages / Domains screens — no DataGrid dependency
 *     today, so we avoid pulling one in).
 *
 * All numbers come from `/api/admin/ai/usage` — the SPA does no math
 * beyond formatting.
 *
 * Responsive at 360 / 768 / 1280 via MUI `<Stack direction>` + `sx`
 * responsive props.
 */

import { useMemo } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { AiUsageDailyBucket, AiUsageResponse } from "@ottopilot/shared";
import { useAiUsage } from "../hooks/useAiStudio";
import { BudgetBanner } from "../features/ai/BudgetBanner";

function formatUsd(value: number): string {
  if (value === 0) return "$0";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

/**
 * Inline-SVG sparkline. Renders the daily-spend series as a polyline +
 * area fill. No external charting library — plain MUI `<Box>` wrapping
 * an inline SVG, per the issue brief: "Recharts/visx is too much for a
 * sparkline."
 *
 * The chart is purely decorative: the operator's source of truth is the
 * numeric display + the table. We use a min-of-zero baseline and scale
 * the y-axis to the maximum bucket so flat months don't render as a
 * uniform straight line at the top.
 */
function DailySpendSparkline({
  data,
  width = 600,
  height = 80,
}: {
  data: AiUsageDailyBucket[];
  width?: number;
  height?: number;
}): ReactElement {
  const padding = 4;
  if (data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="ai-usage-sparkline-empty"
      >
        <Typography variant="caption" color="text.secondary">
          No spend recorded this month yet.
        </Typography>
      </Box>
    );
  }

  const maxValue = Math.max(...data.map((d) => d.costUsd), 0);
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  // Map each bucket to (x, y). When maxValue is 0 we put everything on
  // the baseline.
  const points = data.map((bucket, i) => {
    const x = padding + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const y =
      maxValue === 0 ? padding + innerH : padding + innerH - (bucket.costUsd / maxValue) * innerH;
    return { x, y, bucket };
  });

  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath =
    `${path} L${points[points.length - 1]!.x},${padding + innerH}` +
    ` L${points[0]!.x},${padding + innerH} Z`;

  return (
    <Box
      sx={{ width: "100%", overflow: "hidden" }}
      data-testid="ai-usage-sparkline"
      role="img"
      aria-label={`Daily AI spend, ${data.length} day${data.length === 1 ? "" : "s"}`}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
      >
        <path d={areaPath} fill="rgba(25, 118, 210, 0.12)" />
        <path d={path} fill="none" stroke="#1976d2" strokeWidth={2} strokeLinejoin="round" />
        {points.map((p) => (
          <circle key={p.bucket.date} cx={p.x} cy={p.y} r={2.5} fill="#1976d2" />
        ))}
      </svg>
    </Box>
  );
}

function NumericTile({
  label,
  value,
  caption,
  testId,
}: {
  label: string;
  value: string;
  caption?: string;
  testId: string;
}): ReactElement {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography variant="h2" sx={{ mt: 0.5, fontWeight: 500 }} data-testid={testId}>
        {value}
      </Typography>
      {caption ? (
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      ) : null}
    </Paper>
  );
}

export function AiUsagePage(): ReactElement {
  // Default = current UTC month (the API treats absence as current).
  const usage = useAiUsage(undefined);

  const data: AiUsageResponse | undefined = usage.data;

  const remaining = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, data.budgetUsd - data.spentUsd);
  }, [data]);

  return (
    <Stack spacing={3} data-testid="ai-usage-page">
      <Box>
        <Typography variant="h2">AI usage</Typography>
        <Typography variant="body2" color="text.secondary">
          Month-to-date AI cost across all generations and judges. Numbers refresh every 30 seconds.
        </Typography>
      </Box>

      <BudgetBanner />

      {usage.isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {usage.isError ? (
        <Alert severity="error" data-testid="ai-usage-error">
          {usage.error.message}
        </Alert>
      ) : null}

      {data ? (
        <>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} data-testid="ai-usage-tiles">
            <NumericTile
              label="Spent"
              value={formatUsd(data.spentUsd)}
              caption={`of ${formatUsd(data.budgetUsd)} budget`}
              testId="ai-usage-spent"
            />
            <NumericTile
              label="Remaining"
              value={formatUsd(remaining)}
              caption="this month"
              testId="ai-usage-remaining"
            />
            <NumericTile
              label="Generations"
              value={String(data.generationCount)}
              caption="total in period"
              testId="ai-usage-count"
            />
          </Stack>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" gutterBottom>
              Daily spend
            </Typography>
            <DailySpendSparkline data={data.dailySpend} />
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
              <Typography variant="subtitle2">Top expensive generations</Typography>
              <Typography variant="caption" color="text.secondary">
                10 most expensive AI calls this month
              </Typography>
            </Box>
            <TableContainer>
              <Table aria-label="Top expensive generations" data-testid="ai-usage-top-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Prompt</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topGenerations.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 6 }}
                        data-testid="ai-usage-top-empty"
                      >
                        <Typography variant="body2" color="text.secondary">
                          No generations recorded this month yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.topGenerations.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        data-testid="ai-usage-top-row"
                        data-generation-id={row.id}
                      >
                        <TableCell data-testid="ai-usage-top-row-type">{row.type}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.model ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.promptPrefix}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateShort(row.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" data-testid="ai-usage-top-row-cost">
                          {formatUsd(row.costUsd)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : null}
    </Stack>
  );
}
